import { Button } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateShowFolder } from '~/api/show-folders';
import {
  Dialog,
  DialogContentTitle,
  DialogForm,
  DialogTitle,
} from '~/components/dialogs/components';
import {
  DialogActions,
  DialogContent,
  DialogFieldLabel,
  DialogFieldRequired,
} from '~/components/dialogs/lib/styles';
import { DetailTextField } from '~/components/forms/details';
import { useShowFiltersParams } from '../context/ShowFiltersContext/searchParams';

interface AddFolderDialogProps {
  showFolderId: number;
  openNewFolderDialog: boolean;
  setOpenNewFolderDialog: (val: boolean) => void;
}

export const AddFolderDialog = ({
  showFolderId,
  openNewFolderDialog,
  setOpenNewFolderDialog, // refetch,
}: AddFolderDialogProps) => {
  const { t } = useTranslation();
  const [folderNameTouched, setFolderNameTouched] = useState(false);

  const [folderName, setFolderName] = useState('');

  const folderNameValid = folderName.trim() !== '';

  const [create, createState] = useCreateShowFolder();
  const { tags } = useShowFiltersParams();

  const handleClose = () => {
    setOpenNewFolderDialog(false);
    setFolderNameTouched(false);
    setFolderName('');
    createState.reset();
  };

  const onAddFolder = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await create({
      variables: {
        condition: { tags },
        input: { name: folderName, parentId: showFolderId },
      },
    });
    handleClose();
  };

  return (
    <Dialog open={openNewFolderDialog} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={handleClose}>
        {t('pages.shows.list.dialogs.add_folder.title')}
      </DialogTitle>

      <DialogContent>
        <DialogContentTitle>
          {t('pages.shows.list.dialogs.add_folder.description')}
        </DialogContentTitle>
        <DialogFieldLabel>
          {t('pages.shows.list.dialogs.add_folder.folder_name_label')}
          <DialogFieldRequired>*</DialogFieldRequired>
        </DialogFieldLabel>
        <DialogForm id="new-folder" onSubmit={onAddFolder}>
          <DetailTextField
            autoFocus
            placeholder={t('pages.shows.list.dialogs.add_folder.folder_name_placeholder')}
            onBlur={() => setFolderNameTouched(true)}
            error={folderNameTouched && !folderNameValid}
            value={folderName}
            onChange={(event) => setFolderName(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={!folderNameValid || createState.loading}
          form="new-folder"
          type="submit"
          variant="contained"
        >
          {t('common.dialogs.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
